import { useState, useEffect } from 'react'
import Header from '@components/Header/Header'
import PhoneInput from '@components/PhoneInput/PhoneInput'
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import Snackbar from '@components/Snackbar/Snackbar'
import PackageOption from '@components/PackageOption/PackageOption'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePhoneValidation } from '@hooks/usePhoneValidation'
import { usePpobProducts, PpobProduct } from '@hooks/usePpobProducts'
import { PpobProductCategory } from '@hooks/usePpobServices'
import { setHeaderColor } from '@lib/telegram'
import { WHITE } from '@styles/colors'
import {
  Container,
  ContentContainer,
  PhoneInputWrapper,
  KeyboardSection,
  PackageSection,
  PackageLabel,
  PackageList,
  ButtonWrapper
} from './PhoneData.styles'

const PhoneData = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { validatePhoneNumber, isValidating, normalizePhoneNumber } = usePhoneValidation()
  const { products, isLoading: isLoadingProducts, fetchProducts } = usePpobProducts()

  const state = location.state as { category?: PpobProductCategory } | undefined
  const category = state?.category
  const [step, setStep] = useState<'input' | 'package'>('input')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [normalizedPhoneNumber, setNormalizedPhoneNumber] = useState('')
  const [providerName, setProviderName] = useState<string>()
  const [providerId, setProviderId] = useState<string>()
  const [providerImageUrl, setProviderImageUrl] = useState<string | null>()
  const [selectedPackage, setSelectedPackage] = useState<PpobProduct | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [showSnackbar, setShowSnackbar] = useState(false)

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  const handleKeyboardChange = (value: string | 'delete') => {
    if (value === 'delete') {
      setPhoneNumber(prev => prev.slice(0, -1))
    } else {
      setPhoneNumber(prev => prev + value)
    }
  }

  const handleContinue = async () => {
    if (step === 'input') {
      // Validate phone number first
      const validation = await validatePhoneNumber(phoneNumber, category)

      if (validation.success) {
        // Save normalized phone number for payment
        const normalized = normalizePhoneNumber(phoneNumber)
        setNormalizedPhoneNumber(normalized)

        setProviderName(validation.providerName)
        setProviderId(validation.providerId)
        setProviderImageUrl(validation.providerImageUrl)
        setStep('package')

        // Fetch products for this provider
        if (validation.providerName) {
          await fetchProducts({
            category: category,
            brand: validation.providerName,
            size: 100,
            sortBy: 'sellingPrice'
          })
        }
      } else {
        setSnackbarMessage(t('phoneData.errors.invalidPhoneNumber'))
        setShowSnackbar(true)
      }
    } else {
      // Navigate to payment confirm page
      navigate('/payment-confirm', {
        state: {
          phoneNumber: normalizedPhoneNumber,
          providerName,
          providerImageUrl,
          amount: selectedPackage?.sellingPriceNet,
          product: selectedPackage,
          paymentType: 'prepaid',
          service: 'mobile_data',
          productAmount: selectedPackage?.productAmount,
          adminFee: selectedPackage?.adminFee,
          platformFee: selectedPackage?.platformFee
        }
      })
    }
  }

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
  }

  const handleBack = () => {
    if (step === 'package') {
      setStep('input')
      setSelectedPackage(null)
    } else {
      navigate(-1)
    }
  }

  return (
    <Container>
      <LoadingModal visible={isValidating || isLoadingProducts} />
      <Snackbar
        message={snackbarMessage}
        isVisible={showSnackbar}
        onClose={handleSnackbarClose}
        textAlign="center"
        width="100%"
      />
      <Header
        title={t('phoneData.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <PhoneInputWrapper>
          <PhoneInput
            value={phoneNumber}
            onChange={setPhoneNumber}
            readOnly={step !== 'input'}
            iconType="data"
            providerName={providerName}
            providerImageUrl={providerImageUrl}
          />
        </PhoneInputWrapper>

        {step === 'input' ? (
          <KeyboardSection>
            <NumericKeyboard onChange={handleKeyboardChange} />
            <PrimaryButton
              onClick={handleContinue}
              disabled={!phoneNumber}
            >
              {t('common.continue')}
            </PrimaryButton>
          </KeyboardSection>
        ) : (
          <>
            <PackageSection>
              <PackageLabel>{t('phoneData.choosePackage')}</PackageLabel>
              {products.length > 0 ? (
                <PackageList>
                  {products.map((product) => (
                    <PackageOption
                      key={product.id}
                      title={product.productNameEn}
                      description={product.descEn || ''}
                      price={`${product.sellingPrice.toLocaleString('id-ID')} JIDR`}
                      selected={selectedPackage?.id === product.id}
                      onClick={() => setSelectedPackage(product)}
                    />
                  ))}
                </PackageList>
              ) : (
                <PackageLabel>{t('phoneData.noPackagesAvailable')}</PackageLabel>
              )}
            </PackageSection>
            <ButtonWrapper>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!selectedPackage}
              >
                {t('common.continue')}
              </PrimaryButton>
            </ButtonWrapper>
          </>
        )}
      </ContentContainer>
    </Container>
  )
}

export default PhoneData
